import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowRight, Bookmark } from 'lucide-react';
import { searchDetailSuffix } from '../utils/publicSearch';
import { ImageMetadata, MonthLink, getAdjacentMonthLinks } from '../services/api';
import { getConfig } from '../config';
import { useExtensions } from '../hooks/useExtensions';
import ExtensionSlot from './ExtensionSlot';
import { useContent } from '../contexts/ContentContext';
import { formatDateYmd, toEpochMs } from '../utils/time';
import { imageDetailUrl } from '../utils/image';
import { imageOrigin } from '../utils/illustrationRoute';
import { useImagePreview } from '../contexts/ImagePreviewContext';
import { useVisitorFavorites } from '../hooks/useVisitorFavorites';

type GalleryImage = Pick<ImageMetadata, 'id' | 'hash' | 'time' | 'thumb' | 'published_at' | 'public_title'>;

interface ImageGridProps {
    images: GalleryImage[];
    year: string | null;
    month: string | null;
    word?: string;
    onGridWidthChange?: (width: number) => void;
    sidebarVisible?: boolean;
    showMonthNavigation?: boolean;
    contextHref?: string;
    idPrefix?: string;
}

function ImageCard({ image, images, href, date, anchor, idPrefix, favorited, isNew }: { image: GalleryImage; images: GalleryImage[]; href: string; date: string; anchor: boolean; idPrefix: string; favorited: boolean; isNew: boolean }) {
    const openPreview = useImagePreview();
    const { t } = useContent();
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const origin = new URL(getConfig().apiBaseUrl).origin;
    const thumb = image.thumb ? new URL(image.thumb, origin).href : '';
    const title = image.public_title || t('detail.name', { id: image.id });
    return <article id={`${idPrefix}image-${image.id}`} className="lab-image-card">
        {anchor && <span id={`${idPrefix}date-image-anchor-${date.replace(/\//g, '-')}`} />}
        <a href={href} className="thumbnail-link" onClick={event => openPreview(event, href, { image, items: images, page: Number(new URLSearchParams(window.location.search).get('pageNumber')) || 1 })}>
            {thumb && !failed ? <img key={attempt} src={thumb} alt={title} loading="lazy" onError={() => setFailed(true)} /> : <span className="lab-image-placeholder">{t('common.imageUnavailable')}</span>}
            <span className="lab-image-card__id"><span aria-hidden="true">{image.id}</span><span className="sr-only">{t('illustrations.imageId', { id: image.id })}</span></span>
            {date && <time className="lab-image-card__date" dateTime={date.replace(/\//g, '-')}>{date}</time>}
            {isNew && <span className="lab-image-card__new">{t('illustrations.newBadge')}</span>}
            {favorited && <span className="lab-image-card__saved"><Bookmark size={16} fill="currentColor" strokeWidth={1.5} aria-hidden="true" /><span className="sr-only">{t('illustrations.savedIndicator')}</span></span>}
        </a>
        {failed && <button className="lab-button" onClick={() => { setFailed(false); setAttempt(value => value + 1); }}>{t('common.retry')}</button>}
    </article>;
}

const ImageGrid: React.FC<ImageGridProps> = ({ images, year, month, word, onGridWidthChange, showMonthNavigation = true, contextHref, idPrefix = '' }) => {
    const { t } = useContent();
    const favorites = useVisitorFavorites();
    const { getAdsForPlacement, loading: extensionsLoading } = useExtensions();
    const [adjacent, setAdjacent] = useState<{ newer: MonthLink | null; older: MonthLink | null }>({ newer: null, older: null });
    const container = useRef<HTMLDivElement>(null);
    const params = new URLSearchParams(window.location.search);
    const tags = params.get('tags');
    const color = params.get('color');
    const origin = contextHref || imageOrigin(params) || window.location.pathname + window.location.search + window.location.hash;
    const suffix = searchDetailSuffix() + (tags ? '&tags=' + encodeURIComponent(tags) : color ? '&color=' + encodeURIComponent(color) : word ? '&word=' + encodeURIComponent(word) : '') + '&from=' + encodeURIComponent(origin);
    useEffect(() => {
        let active = true;
        setAdjacent({ newer: null, older: null });
        if (showMonthNavigation && year && month) getAdjacentMonthLinks(year, month).then(value => { if (active) setAdjacent(value); }).catch(() => {});
        return () => { active = false; };
    }, [year, month, showMonthNavigation]);
    useEffect(() => {
        const resize = new ResizeObserver(entries => onGridWidthChange?.(entries[0].contentRect.width));
        if (container.current) resize.observe(container.current);
        return () => resize.disconnect();
    }, [onGridWidthChange]);
    const visibleImages = useMemo(() => images.filter(image => {
        const published = toEpochMs(image.published_at);
        return published === null || published <= Date.now();
    }), [images]);
    const now = Date.now();
    const hash = window.location.hash;
    const favoriteKeys = useMemo(() => new Set(favorites.readable ? favorites.data.images.map(({ id, hash }) => `${id}:${hash}`) : []), [favorites.data, favorites.readable]);
    useEffect(() => {
        const scroll = () => {
            let id = window.location.hash.slice(1);
            try { id = decodeURIComponent(id); } catch {}
            const target = document.getElementById(id) || document.getElementById(id.replace('date-sep-', 'date-image-anchor-'));
            target?.scrollIntoView({ block: 'start', behavior: 'auto' });
        };
        const timer = window.setTimeout(scroll, 100);
        window.addEventListener('hashchange', scroll);
        return () => { clearTimeout(timer); window.removeEventListener('hashchange', scroll); };
    }, [visibleImages, hash]);
    const ads = extensionsLoading ? null : getAdsForPlacement('rakuten.grid');
    let imageCount = 0, adCount = 0;
    const dates = new Set<string>();
    const monthNavigation = (adjacent.newer || adjacent.older) && <nav className="lab-month-links" aria-label={t('tagSearch.dateTitle')}>
        {adjacent.newer && <a className="lab-button inline-flex items-center gap-1" href={`/?page=gallery&year=${adjacent.newer.year}&month=${adjacent.newer.month}`} data-scroll-top="instant"><span>{t('pagination.next')} {adjacent.newer.year}/{adjacent.newer.month}</span><ArrowRight size={16} aria-hidden="true" className="shrink-0" /></a>}
        {adjacent.older && <a className="lab-button inline-flex items-center gap-1" href={`/?page=gallery&year=${adjacent.older.year}&month=${adjacent.older.month}`} data-scroll-top="instant"><span>{t('pagination.previous')} {adjacent.older.year}/{adjacent.older.month}</span><ArrowRight size={16} aria-hidden="true" className="shrink-0" /></a>}
    </nav>;
    return <div ref={container} className="lab-gallery">
        {monthNavigation}
        <div className="lab-image-grid">
                {visibleImages.map(image => {
                    const date = formatDateYmd(image.time) || '';
                    const generatedAt = toEpochMs(image.time);
                    const isNew = generatedAt !== null && generatedAt <= now && now - generatedAt <= 7 * 24 * 60 * 60 * 1000;
                    const anchor = !!date && !dates.has(date);
                    dates.add(date);
                    imageCount++;
                    const ad = ads && imageCount % Math.max(1, ads.interval) === 0
                        ? (ads.fixed[adCount] || ads.loop[(adCount - ads.fixed.length) % ads.loop.length]) : null;
                    if (ad) adCount++;
                    return <React.Fragment key={image.id}>
                        <ImageCard image={image} images={visibleImages} href={imageDetailUrl(image) + (idPrefix.startsWith('preview-') ? '&from=' + encodeURIComponent(origin.split('#')[0] + '#' + idPrefix + 'image-' + image.id) : suffix)} date={date} anchor={anchor} idPrefix={idPrefix} favorited={favoriteKeys.has(`${image.id}:${image.hash}`)} isNew={isNew} />
                        {ad && <ExtensionSlot config={ad} />}
                    </React.Fragment>;
                })}
        </div>
        {monthNavigation}
    </div>;
};
export default ImageGrid;
